import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import React, { useEffect, useState } from "react";
import DOMPurifyFactory from 'dompurify';

const isServer = typeof window === 'undefined';
const DOMPurify = isServer ? { sanitize: (h: string) => h } : DOMPurifyFactory(window);

export function splitSlug(fullSlug: string) {
  if (!fullSlug) return { id: "", slug: "" };
  const parts = fullSlug.split("-");
  if (parts.length === 1) {
    return { id: "p", slug: parts[0] }; // Fallback for slugs without hyphens
  }
  const id = parts.pop() || "";
  const slug = parts.join("-");
  return { id, slug };
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function ClientOnly({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return <>{children}</>;
}

export function getImageUrl(url: string | null) {
  if (!url) return "/placeholder.jpg";
  if (url.startsWith("http")) return url;

  const apiBase = import.meta.env.VITE_API_URL || "/api";
  const baseUrl = apiBase.replace("/api", "");
  return `${baseUrl}${url}`;
}

export function getImageProps(url: string | null, isHero = false) {
  const src = getImageUrl(url);
  if (!url || url.startsWith("http") || !url.includes("opt-")) {
    return { src };
  }

  const apiBase = import.meta.env.VITE_API_URL || "/api";
  const baseUrl = apiBase.replace("/api", "");
  const baseFile = url.replace("-lg.webp", "").replace(".webp", "");

  return {
    src: baseUrl + baseFile + (isHero ? "-lg.webp" : "-md.webp"),
    srcSet:
      baseUrl +
      baseFile +
      "-sm.webp 400w, " +
      baseUrl +
      baseFile +
      "-md.webp 800w, " +
      baseUrl +
      baseFile +
      "-lg.webp 1200w",
    sizes: isHero ? "(max-width: 1200px) 100vw, 1200px" : "(max-width: 800px) 100vw, 800px",
  };
}

export function parseContent(html: string) {
  if (!html) return "";

  // Sanitize the HTML first
  const cleanHtml = DOMPurify.sanitize(html, {
    ADD_TAGS: ["iframe", "oembed", "figure", "figcaption", "table", "thead", "tbody", "tr", "td", "th", "u", "mark", "code", "pre", "span"],
    ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "url", "class", "style", "width", "height", "src", "alt", "title", "href", "target", "rel", "colspan", "rowspan", "span", "dir", "lang", "data-oembed-url"]
  } as any);

  // Perform transformations on sanitized HTML.
  
  // 1. Transform YouTube oembed to iframe
  let transformed = cleanHtml.replace(
    /<p>\s*<oembed url="https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([^"&]+)(?:[^"]*)?"><\/oembed>\s*<\/p>/g,
    '<div class="aspect-video my-12 rounded-2xl overflow-hidden shadow-2xl bg-muted"><iframe width="100%" height="100%" src="https://www.youtube.com/embed/$1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>',
  );

  transformed = transformed.replace(
    /<oembed url="https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([^"&]+)(?:[^"]*)?"><\/oembed>/g,
    '<div class="aspect-video my-12 rounded-2xl overflow-hidden shadow-2xl bg-muted"><iframe width="100%" height="100%" src="https://www.youtube.com/embed/$1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>',
  );

  transformed = transformed.replace(
    /<p>\s*<oembed url="https?:\/\/youtu\.be\/([^"&?]+)(?:[^"]*)?"><\/oembed>\s*<\/p>/g,
    '<div class="aspect-video my-12 rounded-2xl overflow-hidden shadow-2xl bg-muted"><iframe width="100%" height="100%" src="https://www.youtube.com/embed/$1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>',
  );

  transformed = transformed.replace(
    /<oembed url="https?:\/\/youtu\.be\/([^"&?]+)(?:[^"]*)?"><\/oembed>/g,
    '<div class="aspect-video my-12 rounded-2xl overflow-hidden shadow-2xl bg-muted"><iframe width="100%" height="100%" src="https://www.youtube.com/embed/$1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>',
  );

  // 2. Wrap code blocks with a copy button
  return transformed;
}
